import {StoreItemCategory} from '../../../../shared/entity_datas/StoreEnums.ts'
import {UIWStoreBodyController} from './UIWStoreBodyController.ts'
import {ColonistCoinProduct} from '../../../../shared/store/ColonistCoinProduct.ts'
import {UIDCoinProductView} from './UIDCoinProductView.ts'
import {createDivIn} from '../../../shared/scripts/ui_utils/DomUtils.ts'
import * as Item from '../../../../shared/store/StoreItemList.ts'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct.ts'
import {UIDStoreMembershipProductView} from './UIDStoreMembershipProductView.ts'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'
import {UIPopupController} from '../../../shared/scripts/ui_popup/UIPopupController.ts'
import {type TranslationObject} from '@colonist/i18n'
import {ColonistMembershipType} from '@colonist/types'
import {DiscordInstance} from '../discord/DiscordInstance.ts'
import {SDKRequest} from '../../../../shared/DiscordActivityTypes.ts'
import {type UIStoreItemView} from './UIStoreItemView.ts'
import {PaymentMethod} from '../../../../shared/store/BaseColonistStoreProduct.ts'
import {UIDStoreMembershipTabsController} from '../../../shared/scripts/store/UIDStoreMembershipTabsController.ts'
import {ClientUserState} from '../../../shared/scripts/ClientUserState.ts'
import {getManageSubscriptionPopupBody} from '../../../shared/scripts/store/StoreUtils.ts'
import {UIDStoreGiftOverlayController} from './UIDStoreGiftOverlayController.ts'
import {type ReferralReward} from '../../../../shared/ReferralProgram.ts'
import {clientI18n} from '../../../shared/scripts/ClientI18n.ts'
import {ReferralTypeEnum} from '../../../../shared/entity_datas/EntityDatas.ts'

export class UIDStoreBodyController extends UIWStoreBodyController {
    protected override createGiftOverlay() {
        const sceneContainer = document.getElementById('scene_discord_gifting_container') as HTMLDivElement
        this.giftOverlayController = new UIDStoreGiftOverlayController(sceneContainer, this.storeState)
    }

    // eslint-disable-next-line class-methods-use-this
    protected override createMembershipItems() {
        this.membershipsDiv.replaceChildren()
        this.membershipItems.length = 0
        const membershipProducts = ColonistMembershipProduct.getAllMembershipProducts()
        for(const product of membershipProducts) {
            const giftOverlay = UIUtilsEjsVariables.isUserOnDiscordMobile ? undefined : this.giftOverlayController
            const membership = new UIDStoreMembershipProductView(this.membershipsDiv, this.storeState, product, giftOverlay)
            this.membershipItems.push(membership)
        }
        this.membershipTabsController = new UIDStoreMembershipTabsController(this.storeState)
        // To avoid the element occupy the whole width
        createDivIn(this.membershipsDiv, '')
    }

    protected override updateAllCategoryItems() {
        this.items.length = 0
        this.updateCategoryItems(StoreItemCategory.Expansion, this.expansionsDiv)
        this.updateCategoryItems(StoreItemCategory.Map, this.mapsDiv)
        this.updateCategoryItems(StoreItemCategory.Color, this.colorsDiv)
        this.updateCategoryItems(StoreItemCategory.Icon, this.avatarsDiv)

        if(UIUtilsEjsVariables.isUserOnDiscordMobile) {
            this.setUnavailableOnMobileActions()
            return
        }

        if(ClientUserState.userState?.forceSubscription === false) return
        this.setSubscribeClickActions()
    }

    override updateOwnedMembership(ownedMembership: ColonistMembershipProduct | undefined, paymentMethod: PaymentMethod | undefined): void {
        this.createMembershipItems()
        if(ownedMembership == undefined) return

        const disableMembershipManagement = (paymentMethod != PaymentMethod.Discord) && (paymentMethod != PaymentMethod.Stripe)
        for(const membershipView of this.membershipItems) {
            if(disableMembershipManagement) {
                if(membershipView.membershipProduct.type == ownedMembership.type) {
                    membershipView.changeToOwnedButton()
                    continue
                }
                membershipView.removeButton()
                continue
            }

            membershipView.changeToManageButton()
        }
    }

    override updateSubscribedItems(ownedMembership: ColonistMembershipProduct | undefined, paymentMethod: PaymentMethod | undefined): void {
        if(ownedMembership == undefined) return
        switch(ownedMembership.type) {
            case ColonistMembershipType.PlusMonthly:
            case ColonistMembershipType.PlusYearly:
                this.markItemsAsManageSubscription(StoreItemCategory.Map)
                this.markItemsAsManageSubscription(StoreItemCategory.Expansion)
                this.markItemsAsManageSubscription(StoreItemCategory.Color)
                break
            case ColonistMembershipType.Holiday2023AllAccessPass:
            case ColonistMembershipType.PremiumMonthly:
            case ColonistMembershipType.PremiumYearly:
                this.markItemsAsSubscribed(StoreItemCategory.Map)
                this.markItemsAsSubscribed(StoreItemCategory.Expansion)
                this.markItemsAsManageSubscription(StoreItemCategory.Color)
                break
            case ColonistMembershipType.EliteMonthly:
            case ColonistMembershipType.EliteYearly:
                this.markItemsAsSubscribed(StoreItemCategory.Map)
                this.markItemsAsSubscribed(StoreItemCategory.Expansion)
                this.markItemsAsSubscribed(StoreItemCategory.Color)
                break
        }
    }

    protected override markItemsAsSubscribed(category: StoreItemCategory) {
        for(const item of this.items) {
            if(!UIDStoreBodyController.shouldUpdateItemView(item)) continue
            if(item.itemDetail.category != category) continue

            item.markAsSubscribed()
            item.updateAction(() => {
                UIDStoreBodyController.showSubscribedItemPopup()
            })
        }
    }

    private markItemsAsManageSubscription(category: StoreItemCategory) {
        for(const item of this.items) {
            if(!UIDStoreBodyController.shouldUpdateItemView(item)) continue
            if(item.itemDetail.category != category) continue

            item.updateAction(() => {
                UIDStoreBodyController.showManageSubscriptionOnWebPopup()
            })
        }
    }

    protected override createCoinProducts() {
        const coinProducts = ColonistCoinProduct.getAllCoinProducts()
        for(const product of coinProducts) {
            new UIDCoinProductView(this.coinsDiv, product)
        }
    }

    private setSubscribeClickActions() {
        for(const item of this.items) {
            if(!UIDStoreBodyController.shouldUpdateItemView(item)) continue

            if(item.itemDetail.category == StoreItemCategory.Color) {
                item.updateAction(() => {
                    void DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.EliteMonthly)
                })
                continue
            }

            item.updateAction(() => {
                void DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.PremiumMonthly)
            })
        }
    }

    private setUnavailableOnMobileActions() {
        for(const item of this.items) {
            if(!UIDStoreBodyController.shouldUpdateItemView(item)) continue

            item.updateAction(() => {
                UIDStoreBodyController.showUnavailableOnMobilePopup()
            })
        }
    }

    private static shouldUpdateItemView(itemView: UIStoreItemView): boolean {
        const isItemOwned = itemView.isMarkedOwned
        if(isItemOwned) return false

        const isBlackColor = itemView.itemDetail.name.key === new Item.SIColorBlack().name.key
        if(isBlackColor) return false

        const isItemAnIcon = itemView.itemDetail.category === StoreItemCategory.Icon
        if(isItemAnIcon) return false

        return true
    }

    private static showComingSoonPopup() {
        const popupTitle: TranslationObject = {key: 'strings:popups.comingSoon.title'}
        const popupBody: TranslationObject = {key: 'strings:popups.comingSoon.body', options: {context: 'membership'}}
        UIPopupController.createSimplePopup(popupTitle, popupBody)
    }

    static showUnavailableOnMobilePopup() {
        const popupTitle: TranslationObject = {key: 'strings:popups.unavailableOnMobile.title'}
        const popupBody: TranslationObject = {key: 'strings:popups.unavailableOnMobile.body'}
        UIPopupController.createSimplePopup(popupTitle, popupBody)
    }

    private static showSubscribedItemPopup() {
        const popupTitle: TranslationObject = {key: 'strings:storePage.subscribedItem.title'}
        const popupBody: TranslationObject = {key: 'strings:storePage.subscribedItem.body'}
        UIPopupController.createSimplePopup(popupTitle, popupBody)
    }

    private static showManageSubscriptionOnWebPopup() {
        const popupTitle: TranslationObject = {key: 'strings:storePage.manageMembership.title'}
        const popupBody: TranslationObject = getManageSubscriptionPopupBody()
        UIPopupController.createSimplePopup(popupTitle, popupBody)
    }

    // eslint-disable-next-line class-methods-use-this
    override onInviteFriendsButtonClick(referralReward: ReferralReward, userReferralCount: number) {
        DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.ShareActivityLink, {
            message: clientI18n.t('strings:homePage.popups.discordShareActivity.text'),
            referralTypeId: ReferralTypeEnum.DiscordShareActivityReferralProgramStore.toString(),
            analyticsPopupTriggered: referralReward.analyticsPopupTriggered,
            analyticsPopupSuccess: referralReward.analyticsPopupSuccess,
        })
    }
}
